import { useState, useEffect, useRef } from "react";
import { useLocation } from "wouter";
import {
  Activity, CheckCircle2, AlertCircle, Loader2,
  Building2, Mail, Shield, ShieldCheck, ChevronRight, ChevronLeft, Info, Search, MapPin, X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

type Role = "nurse" | "doctor" | "pharmacist" | "physio" | "ot" | "coordinator";

const BASE_URL = import.meta.env.BASE_URL.replace(/\/$/, "");

const JOB_ROLES: Partial<Record<Role, string[]>> = {
  nurse: ["Specialist Nurse", "Advanced Nurse Practitioner", "Nurse Consultant", "Clinical Nurse Specialist", "Staff Nurse", "Charge Nurse / Sister / Charge Nurse Manager", "Other"],
  doctor: ["Consultant", "Registrar ST3–ST7", "Specialty Registrar", "SHO / Core Trainee", "Academic / Research", "Specialty and Associate Specialist (SAS)", "Other"],
  pharmacist: ["Specialist Pharmacist", "Advanced Pharmacist", "Consultant Pharmacist", "Clinical Pharmacist", "Pre-registration Trainee", "Other"],
  physio: ["Specialist Physiotherapist", "Advanced Physiotherapist", "Consultant Physiotherapist", "Band 5 Physiotherapist", "Band 6 Physiotherapist", "Band 7 Physiotherapist", "Academic / Research", "Other"],
  ot: ["Specialist Occupational Therapist", "Advanced Occupational Therapist", "Consultant Occupational Therapist", "Band 5 OT", "Band 6 OT", "Band 7 OT", "Academic / Research", "Other"],
};

const BAND_OPTIONS: Partial<Record<Role, string[]>> = {
  nurse: ["Band 5", "Band 6", "Band 7", "Band 8a", "Band 8b", "Band 8c", "Band 8d", "Band 9", "Other"],
  doctor: ["Foundation Year 1 (FY1)", "Foundation Year 2 (FY2)", "Core Trainee (CT1–CT2)", "ST3–ST5", "ST6–ST7", "Specialty & Associate Specialist (SAS)", "Consultant", "Academic / Research"],
  pharmacist: ["Band 6", "Band 7", "Band 8a", "Band 8b", "Band 8c", "Band 8d", "Pre-registration Trainee", "Other"],
  physio: ["Band 5", "Band 6", "Band 7", "Band 8a", "Band 8b", "Band 8c", "Band 8d", "Other"],
  ot: ["Band 5", "Band 6", "Band 7", "Band 8a", "Band 8b", "Band 8c", "Band 8d", "Other"],
};

const REG_BODY: Partial<Record<Role, { label: string; body: string; hint: string }>> = {
  nurse: { label: "NMC PIN", body: "nmc", hint: "e.g. 12A3456B" },
  doctor: { label: "GMC Number", body: "gmc", hint: "7-digit number, e.g. 1234567" },
  pharmacist: { label: "GPhC Number", body: "gphc", hint: "7-digit number, e.g. 2123456" },
  physio: { label: "HCPC Registration Number", body: "hcpc", hint: "e.g. PH12345" },
  ot: { label: "HCPC Registration Number", body: "hcpc", hint: "e.g. OT12345" },
};

interface OrgResult {
  odsCode: string;
  name: string;
  type?: string;
}

interface SiteResult {
  odsCode: string;
  name: string;
  city?: string;
  postcode?: string;
}

type OtpState = "idle" | "sending" | "sent" | "verified" | "error";
type FormatState = "idle" | "valid" | "invalid";
type VerifyState = "idle" | "checking" | "verified" | "pending" | "failed";

export default function Onboarding() {
  const [, setLocation] = useLocation();
  const role = (localStorage.getItem("rheumhub_role") as Role) ?? "nurse";
  const isCoordinator = role === "coordinator";

  const [step, setStep] = useState<1 | 2 | 3>(1);

  // Step 3 — Practice setup
  const [practiceMode, setPracticeMode] = useState<"create" | "join">("create");
  const [newPracticeName, setNewPracticeName] = useState("");
  const [newPracticeType, setNewPracticeType] = useState<"nhs" | "private" | "independent">("nhs");
  const [inviteLink, setInviteLink] = useState("");
  const [practiceError, setPracticeError] = useState("");
  const [practiceSaving, setPracticeSaving] = useState(false);

  // Work email OTP
  const [workEmail, setWorkEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [otpState, setOtpState] = useState<OtpState>("idle");
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpError, setOtpError] = useState("");
  const [nhsDomainRecognised, setNhsDomainRecognised] = useState<boolean | null>(null);

  // Organisation picker
  const [orgSearch, setOrgSearch] = useState("");
  const [orgResults, setOrgResults] = useState<OrgResult[]>([]);
  const [orgSearching, setOrgSearching] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState<OrgResult | null>(null);
  const [showOrgDropdown, setShowOrgDropdown] = useState(false);
  const orgRef = useRef<HTMLDivElement>(null);

  // Site picker
  const [sites, setSites] = useState<SiteResult[]>([]);
  const [sitesLoading, setSitesLoading] = useState(false);
  const [selectedSite, setSelectedSite] = useState<SiteResult | null>(null);

  // Step 2
  const [jobRole, setJobRole] = useState("");
  const [band, setBand] = useState("");
  const [regNumber, setRegNumber] = useState("");
  const [regFullName, setRegFullName] = useState("");
  const [formatState, setFormatState] = useState<FormatState>("idle");
  const [formatHint, setFormatHint] = useState("");
  const [verifyState, setVerifyState] = useState<VerifyState>("idle");
  const [verifyMessage, setVerifyMessage] = useState("");
  const [reviewRequested, setReviewRequested] = useState(false);
  const [saving, setSaving] = useState(false);

  const regInfo = REG_BODY[role] ?? REG_BODY.nurse!;
  const jobRoles = JOB_ROLES[role] ?? JOB_ROLES.nurse!;
  const bands = BAND_OPTIONS[role] ?? BAND_OPTIONS.nurse!;

  // Close org dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (orgRef.current && !orgRef.current.contains(e.target as Node)) {
        setShowOrgDropdown(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Debounced org search
  useEffect(() => {
    if (!orgSearch.trim() || orgSearch.length < 2) {
      setOrgResults([]);
      return;
    }
    const t = setTimeout(async () => {
      setOrgSearching(true);
      try {
        const res = await fetch(
          `${BASE_URL}/api/organisations?q=${encodeURIComponent(orgSearch.trim())}`,
          { credentials: "include" },
        );
        if (res.ok) {
          const data = await res.json() as OrgResult[];
          setOrgResults(data);
          setShowOrgDropdown(true);
        }
      } catch { /* ignore */ }
      finally { setOrgSearching(false); }
    }, 350);
    return () => clearTimeout(t);
  }, [orgSearch]);

  // Fetch sites when org is selected
  useEffect(() => {
    if (!selectedOrg) { setSites([]); setSelectedSite(null); return; }
    setSitesLoading(true);
    fetch(`${BASE_URL}/api/organisations/${selectedOrg.odsCode}/sites`, { credentials: "include" })
      .then((r) => r.ok ? r.json() as Promise<SiteResult[]> : Promise.resolve([]))
      .then((data) => setSites(data))
      .catch(() => setSites([]))
      .finally(() => setSitesLoading(false));
  }, [selectedOrg]);

  // Inline reg format validation
  useEffect(() => {
    if (!regNumber.trim()) { setFormatState("idle"); setFormatHint(""); return; }
    const t = setTimeout(async () => {
      try {
        const res = await fetch(
          `${BASE_URL}/api/user/validate-reg-format?body=${encodeURIComponent(regInfo.body)}&number=${encodeURIComponent(regNumber.trim())}`,
          { credentials: "include" },
        );
        if (!res.ok) return;
        const data = await res.json() as { valid: boolean | null; hint?: string };
        if (data.valid === null) { setFormatState("idle"); return; }
        setFormatState(data.valid ? "valid" : "invalid");
        setFormatHint(data.hint ?? "");
      } catch { /* ignore */ }
    }, 350);
    return () => clearTimeout(t);
  }, [regNumber, regInfo.body]);

  const verifyRegistrationNow = async () => {
    if (!regNumber.trim() || formatState === "invalid") return;
    setVerifyState("checking");
    setVerifyMessage("");
    setReviewRequested(false);
    try {
      const res = await fetch(`${BASE_URL}/api/user/verify-registration`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          registrationNumber: regNumber.trim(),
          registrationBody: regInfo.body,
          fullName: regFullName.trim() || undefined,
        }),
      });
      const data = await res.json();
      if (res.status === 429) {
        setVerifyState("failed");
        setVerifyMessage(data.error ?? "Too many attempts — please try again in an hour.");
        return;
      }
      if (data.verified) {
        setVerifyState("verified");
        setVerifyMessage(`Registration confirmed against the ${regInfo.label.split(" ")[0]} register.`);
      } else if (data.pending) {
        setVerifyState("pending");
        setVerifyMessage(data.message ?? "Could not confirm automatically — manual review may be needed.");
      } else {
        setVerifyState("failed");
        setVerifyMessage(data.message ?? "Registration not found or lapsed.");
      }
    } catch {
      setVerifyState("pending");
      setVerifyMessage("Could not reach the verification service — please check your connection and try again, or request a manual review.");
    }
  };

  const requestManualReview = async () => {
    try {
      await fetch(`${BASE_URL}/api/user/request-verification-review`, {
        method: "POST",
        credentials: "include",
      });
      setReviewRequested(true);
    } catch { /* ignore */ }
  };

  const sendOtp = async () => {
    if (!workEmail.includes("@")) return;
    setOtpLoading(true);
    setOtpError("");
    try {
      const res = await fetch(`${BASE_URL}/api/user/send-work-email-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email: workEmail }),
      });
      const data = await res.json();
      if (res.ok) {
        setOtpState("sent");
        setNhsDomainRecognised(data.nhsDomainRecognised);
      } else {
        setOtpState("error");
        setOtpError(data.error ?? "Failed to send OTP");
      }
    } catch {
      setOtpState("error");
      setOtpError("Network error — please try again");
    } finally {
      setOtpLoading(false);
    }
  };

  const verifyOtp = async () => {
    setOtpLoading(true);
    setOtpError("");
    try {
      const res = await fetch(`${BASE_URL}/api/user/verify-work-email-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email: workEmail, otp }),
      });
      const data = await res.json();
      if (res.ok && data.verified) {
        setOtpState("verified");
      } else {
        setOtpState("error");
        setOtpError(data.error ?? "Incorrect or expired code — please try again");
      }
    } catch {
      setOtpState("error");
      setOtpError("Network error — please try again");
    } finally {
      setOtpLoading(false);
    }
  };

  const buildOrgPayload = () => ({
    organisationOdsCode: selectedOrg?.odsCode ?? undefined,
    organisationName: selectedOrg?.name ?? undefined,
    siteOdsCode: selectedSite?.odsCode ?? undefined,
    siteName: selectedSite?.name ?? undefined,
  });

  const saveStep1AndAdvance = async () => {
    try {
      await fetch(`${BASE_URL}/api/user/profile`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(buildOrgPayload()),
      });
    } catch { /* non-blocking */ }
    // Coordinators have no clinical registration — skip step 2 (job role, band,
    // registration number) entirely and go straight to practice setup.
    if (isCoordinator) {
      if (!newPracticeName) {
        setNewPracticeName(selectedSite?.name ?? selectedOrg?.name ?? "");
      }
      setStep(3);
      return;
    }
    setStep(2);
  };

  const saveStep2AndAdvance = async () => {
    setSaving(true);
    try {
      await fetch(`${BASE_URL}/api/user/profile`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          jobRole: jobRole || undefined,
          band: band || undefined,
          registrationNumber: regNumber.trim() || undefined,
          registrationBody: regNumber.trim() ? regInfo.body : undefined,
        }),
      });
    } catch { /* non-blocking */ }
    setSaving(false);

    // If user already has a practice (e.g. legacy backfill), skip step 3.
    try {
      const existing = await fetch(`${BASE_URL}/api/practices`, { credentials: "include" });
      if (existing.ok) {
        const list = await existing.json() as Array<{ id: number; membershipStatus?: string }>;
        const hasActive = list.some((p) => (p.membershipStatus ?? "active") === "active");
        if (hasActive) {
          await finishOnboarding();
          return;
        }
      }
    } catch { /* ignore */ }

    // Pre-fill the create form with the chosen organisation name to make step 3 quick.
    if (!newPracticeName) {
      setNewPracticeName(selectedSite?.name ?? selectedOrg?.name ?? "");
    }
    setStep(3);
  };

  const finishOnboarding = async () => {
    try {
      await fetch(`${BASE_URL}/api/user/profile`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ onboardingComplete: true }),
      });
    } catch { /* ignore */ }
    window.location.href = `${BASE_URL}/${role}`;
  };

  const submitPracticeStep = async () => {
    setPracticeError("");
    setPracticeSaving(true);
    try {
      if (practiceMode === "create") {
        if (!newPracticeName.trim()) {
          setPracticeError("Please enter a name for your practice.");
          setPracticeSaving(false);
          return;
        }
        const res = await fetch(`${BASE_URL}/api/practices`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            name: newPracticeName.trim(),
            practiceType: newPracticeType,
            odsCode: selectedSite?.odsCode ?? selectedOrg?.odsCode ?? null,
          }),
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          setPracticeError(data.error ?? "Could not create practice. Please try again.");
          setPracticeSaving(false);
          return;
        }
      } else {
        // Join via invite link. Accept either a full URL or a raw token.
        const trimmed = inviteLink.trim();
        const match = trimmed.match(/practices\/join\/([^/?#\s]+)/);
        const token = match ? match[1] : trimmed;
        if (!token) {
          setPracticeError("Please paste the invite link your practice lead sent you.");
          setPracticeSaving(false);
          return;
        }
        const res = await fetch(`${BASE_URL}/api/practices/join/${encodeURIComponent(token)}`, {
          method: "POST",
          credentials: "include",
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          setPracticeError(data.error ?? "This invite couldn't be accepted. It may have expired or been issued to a different email.");
          setPracticeSaving(false);
          return;
        }
      }
      await finishOnboarding();
    } catch {
      setPracticeError("Network error — please try again.");
      setPracticeSaving(false);
    }
  };

  const canProceedStep1 = otpState === "verified";

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-xl">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <div className="bg-teal-50 p-2.5 rounded-xl"><Activity className="h-6 w-6 text-teal-700" /></div>
          <div>
            <h1 className="text-xl font-bold text-slate-900">Professional Verification</h1>
            <p className="text-sm text-slate-500">Step {step} of 3 — {step === 1 ? "Your organisation" : step === 2 ? "Your professional details" : "Your practice"}</p>
          </div>
          <div className="ml-auto flex gap-2">
            <div className={`h-2 w-8 rounded-full transition-colors ${step >= 1 ? "bg-teal-600" : "bg-slate-200"}`} />
            <div className={`h-2 w-8 rounded-full transition-colors ${step >= 2 ? "bg-teal-600" : "bg-slate-200"}`} />
            <div className={`h-2 w-8 rounded-full transition-colors ${step >= 3 ? "bg-teal-600" : "bg-slate-200"}`} />
          </div>
        </div>

        {/* ── Step 1: Work email + Org picker ── */}
        {step === 1 && (
          <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-8 space-y-6">
            {/* Work email */}
            <div className="flex items-center gap-3">
              <div className="bg-teal-50 p-2 rounded-xl"><Mail className="h-5 w-5 text-teal-700" /></div>
              <h2 className="font-bold text-slate-900 text-lg">Work email verification</h2>
            </div>

            <div className="space-y-3">
              <Label htmlFor="work-email" className="text-sm font-medium text-slate-700">Work or professional email</Label>
              <div className="flex gap-2">
                <Input
                  id="work-email"
                  type="email"
                  placeholder="you@nhs.net"
                  value={workEmail}
                  onChange={(e) => { setWorkEmail(e.target.value); setOtpState("idle"); }}
                  disabled={otpState === "verified"}
                  className="flex-1"
                />
                {otpState !== "verified" && (
                  <Button
                    onClick={sendOtp}
                    disabled={!workEmail.includes("@") || otpLoading}
                    className="bg-teal-700 hover:bg-teal-800 text-white shrink-0"
                  >
                    {otpLoading && otpState !== "sent" && otpState !== "error" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send code"}
                  </Button>
                )}
                {otpState === "verified" && (
                  <div className="flex items-center gap-1.5 text-teal-700 font-medium text-sm px-2">
                    <CheckCircle2 className="h-4 w-4" /> Verified
                  </div>
                )}
              </div>

              {nhsDomainRecognised === false && otpState !== "idle" && (
                <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  This doesn't look like a recognised NHS email domain — that's fine if you work in private practice or academia.
                </p>
              )}
              {nhsDomainRecognised === true && (
                <p className="text-xs text-teal-700 bg-teal-50 border border-teal-200 rounded-lg px-3 py-2 flex items-center gap-1.5">
                  <CheckCircle2 className="h-3.5 w-3.5" /> NHS email domain recognised
                </p>
              )}
            </div>

            {(otpState === "sent" || otpState === "error") && (
              <div className="space-y-3">
                <Label htmlFor="otp" className="text-sm font-medium text-slate-700">Enter the 6-digit code we sent you</Label>
                <div className="flex gap-2">
                  <Input
                    id="otp"
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="123456"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                    className="flex-1 text-center text-xl tracking-[0.3em] font-mono"
                  />
                  <Button
                    onClick={verifyOtp}
                    disabled={otp.length !== 6 || otpLoading}
                    className="bg-teal-700 hover:bg-teal-800 text-white"
                  >
                    {otpLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify"}
                  </Button>
                </div>
                {otpError && <p className="text-xs text-red-600">{otpError}</p>}
                <button onClick={sendOtp} className="text-xs text-teal-700 underline">Resend code</button>
              </div>
            )}

            {/* Organisation picker */}
            <div className="pt-2 border-t border-slate-100 space-y-4">
              <div className="flex items-center gap-3">
                <div className="bg-slate-50 p-2 rounded-xl"><Building2 className="h-5 w-5 text-slate-600" /></div>
                <div>
                  <h3 className="font-semibold text-slate-900">Your organisation</h3>
                  <p className="text-xs text-slate-500">Search by trust name — codes are assigned automatically</p>
                </div>
              </div>

              {/* Trust search */}
              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-700">
                  Trust / organisation <span className="text-slate-400 font-normal">(optional)</span>
                </Label>

                {selectedOrg ? (
                  <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl border border-teal-400 bg-teal-50">
                    <Building2 className="h-4 w-4 text-teal-600 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-teal-900 truncate">{selectedOrg.name}</p>
                      <p className="text-xs text-teal-600">{selectedOrg.odsCode}</p>
                    </div>
                    <button
                      type="button"
                      aria-label="Clear organisation"
                      onClick={() => { setSelectedOrg(null); setOrgSearch(""); setSites([]); setSelectedSite(null); }}
                      className="text-teal-500 hover:text-teal-700 transition-colors"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <div ref={orgRef} className="relative">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
                      <input
                        type="text"
                        value={orgSearch}
                        onChange={(e) => { setOrgSearch(e.target.value); setShowOrgDropdown(true); }}
                        onFocus={() => orgResults.length > 0 && setShowOrgDropdown(true)}
                        placeholder="e.g. Leeds Teaching Hospitals"
                        className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-slate-200 bg-white text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500"
                        aria-label="Search for your NHS trust or organisation"
                      />
                      {orgSearching && (
                        <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-slate-400" />
                      )}
                    </div>

                    {showOrgDropdown && orgResults.length > 0 && (
                      <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden max-h-56 overflow-y-auto">
                        {orgResults.map((org) => (
                          <button
                            key={org.odsCode}
                            type="button"
                            onClick={() => {
                              setSelectedOrg(org);
                              setOrgSearch("");
                              setShowOrgDropdown(false);
                            }}
                            className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-teal-50 text-left transition-colors"
                          >
                            <Building2 className="h-4 w-4 text-slate-400 shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-slate-900 truncate">{org.name}</p>
                              {org.type && <p className="text-xs text-slate-400">{org.type}</p>}
                            </div>
                            <code className="text-xs text-slate-400 shrink-0">{org.odsCode}</code>
                          </button>
                        ))}
                      </div>
                    )}

                    {showOrgDropdown && orgSearch.length >= 2 && !orgSearching && orgResults.length === 0 && (
                      <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-lg px-4 py-3 text-sm text-slate-500">
                        No organisations found. Your admin can add your trust manually.
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Site picker */}
              {selectedOrg && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700">
                    Site / location <span className="text-slate-400 font-normal">(optional)</span>
                  </Label>

                  {sitesLoading ? (
                    <div className="flex items-center gap-2 text-slate-400 text-sm py-1">
                      <Loader2 className="h-4 w-4 animate-spin" /> Loading sites…
                    </div>
                  ) : sites.length === 0 ? (
                    <p className="text-xs text-slate-400 py-1">No sites listed for this trust — your admin can add them.</p>
                  ) : selectedSite ? (
                    <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl border border-teal-400 bg-teal-50">
                      <MapPin className="h-4 w-4 text-teal-600 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-teal-900 truncate">{selectedSite.name}</p>
                        {(selectedSite.city || selectedSite.postcode) && (
                          <p className="text-xs text-teal-600">{[selectedSite.city, selectedSite.postcode].filter(Boolean).join(", ")}</p>
                        )}
                      </div>
                      <button
                        type="button"
                        aria-label="Clear site"
                        onClick={() => setSelectedSite(null)}
                        className="text-teal-500 hover:text-teal-700 transition-colors"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="grid gap-1.5">
                      {sites.map((site) => (
                        <button
                          key={site.odsCode}
                          type="button"
                          onClick={() => setSelectedSite(site)}
                          className="flex items-center gap-3 px-3 py-2.5 rounded-xl border border-slate-200 bg-white hover:border-teal-400 hover:bg-teal-50/50 transition-colors text-left"
                        >
                          <MapPin className="h-4 w-4 text-slate-400 shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-slate-800">{site.name}</p>
                            {(site.city || site.postcode) && (
                              <p className="text-xs text-slate-400">{[site.city, site.postcode].filter(Boolean).join(", ")}</p>
                            )}
                          </div>
                          <code className="text-xs text-slate-400 shrink-0">{site.odsCode}</code>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="flex justify-between pt-2">
              <Button variant="ghost" onClick={() => setLocation(`/${role}`)} className="text-slate-500">
                Skip for now
              </Button>
              <Button
                onClick={saveStep1AndAdvance}
                disabled={!canProceedStep1}
                className="bg-teal-700 hover:bg-teal-800 text-white gap-1.5"
              >
                Next <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {/* ── Step 2: Professional details ── */}
        {step === 2 && (
          <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-8 space-y-6">
            <div className="flex items-center gap-3">
              <div className="bg-teal-50 p-2 rounded-xl"><Shield className="h-5 w-5 text-teal-700" /></div>
              <h2 className="font-bold text-slate-900 text-lg">Professional details</h2>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-700">Job role</Label>
                <select
                  value={jobRole}
                  onChange={(e) => setJobRole(e.target.value)}
                  className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  <option value="">Select role...</option>
                  {jobRoles.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-700">Band / grade</Label>
                <select
                  value={band}
                  onChange={(e) => setBand(e.target.value)}
                  className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  <option value="">Select band...</option>
                  {bands.map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>
            </div>

            <div className="pt-2 border-t border-slate-100 space-y-4">
              <div className="flex items-center gap-3">
                <div className="bg-slate-50 p-2 rounded-xl"><Shield className="h-5 w-5 text-slate-600" /></div>
                <div>
                  <h3 className="font-semibold text-slate-900">Registration number</h3>
                  <p className="text-xs text-slate-500">Checked live against the public UK regulator register</p>
                </div>
              </div>

              <div className="flex items-start gap-2.5 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-blue-800 text-xs">
                <Info className="h-4 w-4 shrink-0 mt-0.5 text-blue-500" />
                <p>
                  We check your {regInfo.label} against the public {regInfo.body.toUpperCase()} register in real time. If the register can't confirm it, you can request a manual review and we'll respond within 2 working days.
                </p>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-700">
                  Full name (as registered) <span className="text-slate-400 font-normal">(optional)</span>
                </Label>
                <Input
                  placeholder="e.g. Jane Smith"
                  value={regFullName}
                  onChange={(e) => {
                    setRegFullName(e.target.value);
                    if (verifyState !== "idle") { setVerifyState("idle"); setVerifyMessage(""); }
                  }}
                  disabled={verifyState === "checking" || verifyState === "verified"}
                />
                <p className="text-xs text-slate-400">Used to cross-check your name on the register — recommended.</p>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-700">{regInfo.label} <span className="text-slate-400 font-normal">(optional)</span></Label>
                <div className="flex gap-2">
                  <Input
                    placeholder={regInfo.hint}
                    value={regNumber}
                    onChange={(e) => {
                      setRegNumber(e.target.value);
                      setFormatState("idle");
                      if (verifyState !== "idle") { setVerifyState("idle"); setVerifyMessage(""); }
                    }}
                    disabled={verifyState === "checking" || verifyState === "verified"}
                    className={`flex-1 font-mono ${formatState === "valid" ? "border-teal-400 focus-visible:ring-teal-400" : formatState === "invalid" ? "border-red-300 focus-visible:ring-red-300" : ""}`}
                  />
                  {verifyState !== "verified" && (
                    <Button
                      onClick={verifyRegistrationNow}
                      disabled={!regNumber.trim() || verifyState === "checking" || formatState === "invalid"}
                      className="bg-teal-700 hover:bg-teal-800 text-white shrink-0"
                    >
                      {verifyState === "checking" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify"}
                    </Button>
                  )}
                </div>
                {formatState === "invalid" && (
                  <p className="text-xs text-red-600 flex items-center gap-1.5">
                    <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                    {formatHint}
                  </p>
                )}
                {formatState === "valid" && verifyState === "idle" && (
                  <p className="text-xs text-teal-700 flex items-center gap-1.5">
                    <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
                    Format looks correct — click Verify to check against the register.
                  </p>
                )}
              </div>

              {verifyState === "verified" && (
                <div className="flex items-center gap-2 bg-teal-50 border border-teal-200 rounded-xl px-4 py-3 text-teal-800 text-sm font-medium">
                  <ShieldCheck className="h-5 w-5 text-teal-600 shrink-0" />
                  {verifyMessage}
                </div>
              )}

              {verifyState === "pending" && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 space-y-2">
                  <div className="flex items-start gap-2 text-amber-800 text-sm">
                    <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                    <p>{verifyMessage}</p>
                  </div>
                  {!reviewRequested ? (
                    <button onClick={requestManualReview} className="text-xs text-amber-700 underline font-medium">
                      Request manual review
                    </button>
                  ) : (
                    <p className="text-xs text-amber-700 font-medium">
                      Manual review requested — we'll respond within 2 working days.
                    </p>
                  )}
                </div>
              )}

              {verifyState === "failed" && (
                <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 space-y-2">
                  <div className="flex items-start gap-2 text-red-800 text-sm">
                    <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium">Registration not confirmed</p>
                      <p className="mt-0.5 text-xs text-red-700">{verifyMessage} Please check your details match the public register exactly.</p>
                    </div>
                  </div>
                  {!reviewRequested ? (
                    <button onClick={requestManualReview} className="text-xs text-red-700 underline font-medium">
                      Request manual review instead
                    </button>
                  ) : (
                    <p className="text-xs text-red-700 font-medium">Manual review requested — we'll be in touch.</p>
                  )}
                </div>
              )}

              <p className="text-xs text-slate-400">
                You can skip this and verify later from your profile page. Maximum 5 verification attempts per hour.
              </p>
            </div>

            <div className="flex justify-between pt-2">
              <Button variant="ghost" onClick={() => setStep(1)} className="text-slate-500 gap-1.5">
                <ChevronLeft className="h-4 w-4" /> Back
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setLocation(`/${role}`)} className="text-slate-600">
                  Skip for now
                </Button>
                <Button onClick={saveStep2AndAdvance} disabled={saving || formatState === "invalid"} className="bg-teal-700 hover:bg-teal-800 text-white gap-1.5">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <ChevronRight className="h-4 w-4" />}
                  Next
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* ── Step 3: Practice setup ── */}
        {step === 3 && (
          <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-8 space-y-6">
            <div className="flex items-center gap-3">
              <div className="bg-teal-50 p-2 rounded-xl"><Building2 className="h-5 w-5 text-teal-700" /></div>
              <div>
                <h2 className="font-bold text-slate-900 text-lg">Your practice workspace</h2>
                <p className="text-xs text-slate-500">
                  Patient lists, advice-line queues and team inboxes live inside a practice. You can switch between several later.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setPracticeMode("create")}
                className={`px-4 py-3 rounded-xl border text-sm font-medium transition-colors ${
                  practiceMode === "create"
                    ? "border-teal-600 bg-teal-50 text-teal-800"
                    : "border-slate-200 text-slate-600 hover:bg-slate-50"
                }`}
                data-testid="practice-mode-create"
              >
                Create a new practice
              </button>
              <button
                type="button"
                onClick={() => setPracticeMode("join")}
                className={`px-4 py-3 rounded-xl border text-sm font-medium transition-colors ${
                  practiceMode === "join"
                    ? "border-teal-600 bg-teal-50 text-teal-800"
                    : "border-slate-200 text-slate-600 hover:bg-slate-50"
                }`}
                data-testid="practice-mode-join"
              >
                Join with invite link
              </button>
            </div>

            {practiceMode === "create" && (
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium text-slate-700">Practice name</Label>
                  <Input
                    placeholder="e.g. Leeds Teaching Hospitals — Rheumatology"
                    value={newPracticeName}
                    onChange={(e) => setNewPracticeName(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium text-slate-700">Practice type</Label>
                  <select
                    value={newPracticeType}
                    onChange={(e) => setNewPracticeType(e.target.value as "nhs" | "private" | "independent")}
                    className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm"
                  >
                    <option value="nhs">NHS trust</option>
                    <option value="private">Private clinic</option>
                    <option value="independent">Independent practice</option>
                  </select>
                </div>
                <p className="text-xs text-slate-500">
                  You'll become the practice <span className="font-medium">lead</span> and can invite colleagues from the practice menu later.
                </p>
              </div>
            )}

            {practiceMode === "join" && (
              <div className="space-y-3">
                <Label className="text-sm font-medium text-slate-700">Invite link</Label>
                <Input
                  placeholder="https://…/practices/join/abc123"
                  value={inviteLink}
                  onChange={(e) => setInviteLink(e.target.value)}
                />
                <p className="text-xs text-slate-500">
                  Paste the link your practice lead sent you. The invite must be addressed to your account email.
                </p>
              </div>
            )}

            {practiceError && (
              <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 flex items-start gap-1.5">
                <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                {practiceError}
              </p>
            )}

            <div className="flex justify-between pt-2">
              <Button variant="ghost" onClick={() => setStep(2)} className="text-slate-500 gap-1.5">
                <ChevronLeft className="h-4 w-4" /> Back
              </Button>
              <Button
                onClick={submitPracticeStep}
                disabled={practiceSaving}
                className="bg-teal-700 hover:bg-teal-800 text-white gap-1.5"
              >
                {practiceSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                {practiceMode === "create" ? "Create practice & finish" : "Accept invite & finish"}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
